/**
 * Lazy seam for `TerminalView` — the only module that reaches xterm.js
 * (`@xterm/xterm` + the fit/search/webgl addons and `xterm.css`, ~390 KB
 * together). The terminal panel is collapsed at boot and renders no view until
 * a session exists, so `TerminalPanel` renders this host instead of importing
 * `TerminalView` directly: xterm lands in its own chunk that is fetched when
 * the first terminal session is actually opened.
 *
 * Mirrors `workspace/spatial/map/SpatialMapLazy.tsx`, including its
 * `RandomDotmSpinner` fallback (repo rule — never `Loader2`).
 */

import { forwardRef, lazy, Suspense } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { TerminalViewProps, TerminalViewRef } from './TerminalView';

export type { TerminalViewProps, TerminalViewRef } from './TerminalView';

const TerminalViewLazy = lazy(() =>
  import('./TerminalView').then((m) => ({ default: m.TerminalView })),
);

/** Suspense-wrapped host — the ergonomic entry point for a terminal session. */
export const TerminalViewHost = forwardRef<TerminalViewRef, TerminalViewProps>(
  function TerminalViewHost(props, ref) {
    return (
      <Suspense
        fallback={
          <div
            className="flex h-full w-full items-center justify-center"
            data-testid="terminal-view-loading"
          >
            <RandomDotmSpinner size={24} />
          </div>
        }
      >
        <TerminalViewLazy {...props} ref={ref} />
      </Suspense>
    );
  },
);
